<script setup>
import { defineProps, defineEmits, ref } from 'vue'

const emit = defineEmits(['value'])

const props = defineProps({
  name: String,
  description: String,
  required: Boolean,
  value: Array,           // Array of strings (IPs)
  placeholder: String,
  itemType: {             // 'ip' for IP validation
    type: String,
    default: 'string'
  }
})

const inputValue = ref('')
const errorMessage = ref('')

// IP validation regex (supports IPv4)
function isValidIP(ip) {
  const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
  return ipv4Regex.test(ip.trim())
}

function addItem() {
  const trimmedValue = inputValue.value.trim()

  if (!trimmedValue) {
    errorMessage.value = 'Please enter a value'
    return
  }

  // Validate IP if itemType is 'ip'
  if (props.itemType === 'ip' && !isValidIP(trimmedValue)) {
    errorMessage.value = 'Invalid IP address format'
    return
  }

  // Check for duplicates
  if (props.value.includes(trimmedValue)) {
    errorMessage.value = 'This value has already been added'
    return
  }

  const newValue = [...props.value, trimmedValue]
  emit('value', newValue)

  // Clear input and error
  inputValue.value = ''
  errorMessage.value = ''
}

function removeItem(index) {
  const newValue = [...props.value]
  newValue.splice(index, 1)
  emit('value', newValue)
}

function handleKeydown(event) {
  if (event.key === 'Enter') {
    event.preventDefault()
    addItem()
  }
}

function clearError() {
  errorMessage.value = ''
}
</script>

<template>
  <div>
    <div class="param-container">
      <div class="param-header">
        <span class="me-2">{{ props.name }}</span>
        <span class="badge text-bg-info">array</span>
        <span v-if="props.required" class="ms-2 badge text-bg-danger">required</span>
      </div>
      <p class="param-description">{{ props.description }}</p>

      <!-- Input field for adding items -->
      <div class="input-group">
        <input
          v-model="inputValue"
          @keydown="handleKeydown"
          @input="clearError"
          type="text"
          class="item-input"
          :class="{ 'has-error': errorMessage }"
          :placeholder="props.placeholder || 'Enter value and press Add'"
        />
        <button @click="addItem" class="btn-add" type="button">
          <font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
          Add
        </button>
      </div>

      <!-- Error message -->
      <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>

      <!-- List of added items -->
      <div v-if="props.value.length > 0" class="items-list">
        <div
          v-for="(item, index) in props.value"
          :key="index"
          class="item-tag"
        >
          <span class="item-value">{{ item }}</span>
          <button @click="removeItem(index)" class="btn-remove" type="button">
            <font-awesome-icon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        No items added yet
      </div>
    </div>
  </div>
</template>

<style scoped>
.param-container {
  padding: 1rem;
  border-radius: 10px;
  border: 1px solid #313946;
  background-color: transparent;
}

.param-header {
  display: flex;
  align-items: center;
  font-weight: normal;
  margin-bottom: 0.5rem;
}

.param-description {
  color: #6d7785;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.input-group {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.item-input {
  flex: 1;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #313946;
  background-color: transparent;
  color: #ffffff;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.item-input:focus {
  outline: none;
  border-color: #6B7280;
  box-shadow: 0 0 0 3px rgba(107, 114, 128, 0.1);
}

.item-input.has-error {
  border-color: #dc3545;
}

.item-input::placeholder {
  color: #6d7785;
}

.btn-add {
  padding: 0.75rem 1.25rem;
  background-color: #374151;
  color: #E5E7EB;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.btn-add:hover {
  background-color: #4B5563;
}

.btn-add:active {
  transform: scale(0.98);
}

.error-message {
  color: #dc3545;
  font-size: 0.85rem;
  margin: 0.25rem 0 0.75rem 0;
}

.items-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #313946;
}

.item-tag {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: rgba(75, 85, 99, 0.3);
  border: 1px solid rgba(107, 114, 128, 0.4);
  border-radius: 6px;
  font-size: 0.875rem;
  color: #9CA3AF;
  font-family: 'Monaco', 'Courier New', monospace;
}

.item-value {
  user-select: all;
}

.btn-remove {
  background: none;
  border: none;
  color: #6B7280;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  transition: all 0.2s ease;
}

.btn-remove:hover {
  background-color: rgba(107, 114, 128, 0.3);
  color: #E5E7EB;
}

.empty-state {
  margin-top: 1rem;
  padding: 1rem;
  text-align: center;
  color: #6d7785;
  font-size: 0.875rem;
  border-top: 1px solid #313946;
}

.badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

.text-bg-info {
  background-color: #17a2b8 !important;
  color: white !important;
}

.text-bg-danger {
  background-color: #dc3545 !important;
  color: white !important;
}
</style>
